import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePaymentScreen } from "./_hooks/usePaymentScreen";
import "./PaymentScreen.scss";
import { Tab, Tabs } from "react-bootstrap";

const PaymentScreen = () => {
  const navigate = useNavigate();
  const { orderDetails, paymentQRDetails, handlePaymentConfirmation } =
    usePaymentScreen();

  const isOnlineQREnabled = Boolean(
    paymentQRDetails?.qrImageUrl && paymentQRDetails?.isQREnabled !== false
  );

  const [paymentMode, setPaymentMode] = useState(() =>
    isOnlineQREnabled ? "UPI" : "COUNTER"
  );

  useEffect(() => {
    if (!isOnlineQREnabled) {
      setPaymentMode("COUNTER");
    }
  }, [isOnlineQREnabled]);

  if (!orderDetails) {
    return (
      <div className="payment-screen d-flex flex-column align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary mb-3" role="status"></div>
        <p className="fw-semibold text-muted">Loading your order details...</p>
      </div>
    );
  }

  const {
    cartItems = [],
    pickUpCartItems = [],
    totalAmount = 0,
    pickUpTotal = 0,
    dineInTotal = 0,
    status = "Pending",
    token = "",
    discount = {},
    tax = {},
  } = orderDetails || {};

  const isCompleted = status === "COMPLETED";

  const handleClearAndNewOrder = () => {
    localStorage.removeItem("currentOrder");
    localStorage.removeItem("currentOrderToken");
    navigate("/list/0");
  };

  return (
    <div className="payment-screen">
      {/* Header Navigation Bar */}
      <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
        <button
          className="btn btn-sm btn-outline-dark fw-bold rounded-pill px-3 d-flex align-items-center gap-1"
          onClick={() => navigate("/")}
        >
          ← Back to Menu
        </button>
        <div className="text-end">
          <span className="badge bg-primary fs-6 px-3 py-1 pill-token">
            Token #{token}
          </span>
        </div>
      </div>

      {status === "PAYMENT_CONFIRMATION" ? (
        <div className="payment-status-box text-center p-4 bg-light rounded-3 border my-3">
          <div className="icon-box display-4 mb-2">💸</div>
          <div className="text-box">
            <h4 className="fw-bold text-dark">Payment Verification Pending</h4>
            <p className="text-muted small mb-3">
              Your payment notification has been sent to the reception. We'll update your order status shortly.
            </p>
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill px-4"
              onClick={() => navigate("/")}
            >
              Browse Menu While You Wait ➔
            </button>
          </div>
        </div>
      ) : isCompleted ? (
        <div className="text-center p-4 bg-success bg-opacity-10 border border-success rounded-3 my-3">
          <div className="display-4 mb-2">🎉</div>
          <h4 className="fw-bold text-success">Order & Payment Completed!</h4>
          <p className="text-muted small mb-3">
            Thank you for dining with us! We hope you enjoyed your meal.
          </p>
          <button
            className="btn btn-success fw-bold rounded-pill px-4 py-2"
            onClick={handleClearAndNewOrder}
          >
            Place Another Order 🛒
          </button>
        </div>
      ) : (
        <div>
          {/* Status Header */}
          <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-3 rounded-3 border">
            <div>
              <span className="text-muted small d-block">Order Status</span>
              <span className={`status-pill ${status?.toLowerCase().replaceAll("_", "-")}`}>
                {status?.replaceAll("_", " ")}
              </span>
            </div>
            <button
              className="btn btn-sm btn-primary fw-semibold rounded-pill px-3"
              onClick={() => navigate("/list/0")}
            >
              + Order More Items
            </button>
          </div>

          {/* Payment Options Selector */}
          <div className="payment-options-container mb-4">
            <h5 className="fw-bold text-dark mb-3">
              {isOnlineQREnabled ? "Choose How to Pay" : "Payment Method"}
            </h5>

            {isOnlineQREnabled && (
              <div className="payment-mode-tabs d-flex gap-2 mb-3">
                <button
                  type="button"
                  className={`payment-mode-btn flex-fill py-2 px-3 fw-bold rounded-3 ${
                    paymentMode === "UPI" ? "active" : ""
                  }`}
                  onClick={() => setPaymentMode("UPI")}
                >
                  📱 Online UPI QR
                </button>
                <button
                  type="button"
                  className={`payment-mode-btn flex-fill py-2 px-3 fw-bold rounded-3 ${
                    paymentMode === "COUNTER" ? "active" : ""
                  }`}
                  onClick={() => setPaymentMode("COUNTER")}
                >
                  💵 Pay at Counter / Cash
                </button>
              </div>
            )}

            {/* Payment Mode A: UPI QR Code */}
            {paymentMode === "UPI" && (
              <div className="upi-payment-box text-center p-3 border rounded-3 bg-white shadow-sm">
                {paymentQRDetails?.qrImageUrl && paymentQRDetails?.isQREnabled !== false ? (
                  <div>
                    <p className="small text-muted mb-2">Scan & Pay via GPay, PhonePe, Paytm or any UPI App</p>
                    <div className="payment-qr my-2">
                      <img
                        src={paymentQRDetails.qrImageUrl}
                        alt="Payment QR Code"
                        style={{ maxWidth: "220px", width: "100%", borderRadius: "12px", border: "1px solid #e2e8f0" }}
                      />
                    </div>
                    <button
                      className="checkout-btn text-uppercase w-100 mt-3"
                      onClick={handlePaymentConfirmation}
                    >
                      Click Here After Payment
                    </button>
                  </div>
                ) : (
                  <div className="py-3">
                    <p className="fw-bold text-dark mb-1">Online QR is Offline</p>
                    <p className="small text-muted mb-0">
                      Please pay via Cash or Card directly at the Cash Counter / Reception.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Payment Mode B: Cash / Card at Counter */}
            {paymentMode === "COUNTER" && (
              <div className="counter-payment-box p-3 border border-warning bg-warning bg-opacity-10 rounded-3 text-start">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="fs-4">💵</span>
                  <h6 className="fw-bold text-dark mb-0">Pay Cash / Card at Reception</h6>
                </div>
                <p className="small text-dark mb-2">
                  Your order <strong>Token #{token}</strong> has been sent to the kitchen. You can pay directly at the Cashier Desk using Cash or Card when leaving or receiving your food.
                </p>
                <div className="d-flex justify-content-between align-items-center bg-white p-2 rounded border">
                  <small className="text-muted">Total Payable at Counter:</small>
                  <strong className="fs-5 text-success">₹{totalAmount}</strong>
                </div>
              </div>
            )}
          </div>

          <hr className="my-4" />

          {/* Order Summary & Itemized Breakdown */}
          <h5 className="fw-bold text-dark mb-3">Order Summary</h5>

          <Tabs
            defaultActiveKey="dineIn"
            id="orderTabs"
            variant="tabs"
            className="mt-2 underline-tabs mb-3"
          >
            <Tab eventKey="dineIn" title={`Dine In (₹${dineInTotal})`}>
              <div className="cart-items">
                {cartItems.length > 0 ? (
                  cartItems.map((item) => (
                    <div key={item?._id} className="cart-item py-2 border-bottom">
                      <img
                        src={item?.itemId?.image || ""}
                        alt={item?.itemId?.name || "Item"}
                        style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "8px" }}
                      />
                      <div className="item-details ms-2">
                        <p className="item-name fw-bold mb-0 text-dark">
                          {item?.itemId?.name || "Unnamed Item"}
                        </p>
                        <small className="text-muted">
                          ₹{item?.itemId?.price || 0} × {item?.quantity || 0}
                        </small>
                      </div>
                      <div className="quantity fw-bold text-dark ms-auto">
                        ₹{(item?.quantity || 0) * (item?.itemId?.price || 0)}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted small py-2">No Dine In items in this order.</p>
                )}
              </div>
            </Tab>

            <Tab eventKey="pickUp" title={`Takeaway / Pick Up (₹${pickUpTotal})`}>
              <div className="cart-items">
                {pickUpCartItems.length > 0 ? (
                  pickUpCartItems.map((item) => (
                    <div key={item?._id} className="cart-item py-2 border-bottom">
                      <img
                        src={item?.itemId?.image || ""}
                        alt={item?.itemId?.name || "Item"}
                        style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "8px" }}
                      />
                      <div className="item-details ms-2">
                        <p className="item-name fw-bold mb-0 text-dark">
                          {item?.itemId?.name || "Unnamed Item"}
                        </p>
                        <small className="text-muted">
                          ₹{item?.itemId?.price || 0} × {item?.quantity || 0}
                        </small>
                      </div>
                      <div className="quantity fw-bold text-dark ms-auto">
                        ₹{(item?.quantity || 0) * (item?.itemId?.price || 0)}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted small py-2">No Takeaway / Pick Up items in this order.</p>
                )}
              </div>
            </Tab>
          </Tabs>

          {/* Order Total */}
          <div className="order-total bg-light p-3 rounded-3 border mt-3">
            <div className="d-flex justify-content-between mb-1 text-muted small">
              <span>Dine In Subtotal:</span>
              <span>₹{dineInTotal}</span>
            </div>
            <div className="d-flex justify-content-between mb-1 text-muted small">
              <span>Pick Up Subtotal:</span>
              <span>₹{pickUpTotal}</span>
            </div>
            {discount?.amount > 0 && (
              <div className="d-flex justify-content-between mb-1 text-danger small">
                <span>Discount:</span>
                <span>-₹{discount.amount}</span>
              </div>
            )}
            {tax?.totalTax > 0 && (
              <div className="d-flex justify-content-between mb-1 text-muted small">
                <span>GST:</span>
                <span>+₹{tax.totalTax}</span>
              </div>
            )}
            <hr className="my-2" />
            <div className="d-flex justify-content-between fs-5 fw-bold text-dark">
              <span>Total Amount:</span>
              <span className="text-primary">₹{totalAmount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentScreen;
